import React, { useEffect, useState } from "react";
import { RefreshCcw, Plane, Stamp, Inbox, Trash2, Phone } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const STATUS = [
    ["new", "Yeni"],
    ["contacted", "İletişime Geçildi"],
    ["handled", "İşlemde"],
    ["closed", "Kapandı"],
];
const STATUS_COLOR = {
    new: "text-gold border-gold/60 bg-gold/10",
    contacted: "text-sky-300 border-sky-500/50 bg-sky-900/20",
    handled: "text-emerald-300 border-emerald-500/50 bg-emerald-900/20",
    closed: "text-muted_green border-border_green",
};
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("tr-TR"); } catch { return iso || "—"; } };

const AdminServiceRequests = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/service-requests");
            setItems(data);
        } catch { toast.error("Talepler yüklenemedi"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const setStatus = async (r, status) => {
        try {
            await api.patch(`/admin/service-requests/${r.id}`, { status });
            setItems((cur) => cur.map((x) => (x.id === r.id ? { ...x, status } : x)));
            toast.success("Durum güncellendi");
        } catch { toast.error("Güncellenemedi"); }
    };

    const remove = async (r) => {
        if (!window.confirm("Bu talep silinsin mi?")) return;
        try {
            await api.delete(`/admin/service-requests/${r.id}`);
            setItems((cur) => cur.filter((x) => x.id !== r.id));
            toast.success("Silindi");
        } catch { toast.error("Silinemedi"); }
    };

    const filtered = filter === "all" ? items : items.filter((r) => r.service_type === filter);
    const counts = {
        flight: items.filter((r) => r.service_type === "flight").length,
        visa: items.filter((r) => r.service_type === "visa").length,
    };

    return (
        <div>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <h1 className="font-serif text-4xl mb-2">Hizmet Talepleri</h1>
                    <p className="text-muted_green text-sm">Uçak bileti ve vize fiyat talepleri — müşteriler tarih/kişi seçip iletişime geçiyor.</p>
                </div>
                <button onClick={load} className="btn-outline-gold !text-xs" data-testid="refresh-service-requests"><RefreshCcw className="w-3.5 h-3.5" /> Yenile</button>
            </div>

            <div className="flex items-center gap-2 mb-6">
                {[["all", `Tümü (${items.length})`], ["flight", `Uçak Bileti (${counts.flight})`], ["visa", `Vize (${counts.visa})`]].map(([k, label]) => (
                    <button
                        key={k}
                        onClick={() => setFilter(k)}
                        className={`text-xs uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${filter === k ? "bg-gold text-emerald_deep border-gold" : "border-border_green text-muted_green hover:border-gold hover:text-gold"}`}
                        data-testid={`sr-filter-${k}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-muted_green text-sm">Yükleniyor…</div>
            ) : filtered.length === 0 ? (
                <div className="border border-dashed border-border_green p-12 text-center">
                    <Inbox className="w-12 h-12 text-muted_green mx-auto mb-3" />
                    <div className="text-muted_green text-sm">Henüz talep yok.</div>
                </div>
            ) : (
                <div className="space-y-3">
                    {filtered.map((r) => (
                        <div key={r.id} className="border border-border_green bg-emerald_surface p-5" data-testid={`sr-${r.id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <span className="text-gold flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.12em]">
                                            {r.service_type === "flight" ? <Plane className="w-4 h-4" /> : <Stamp className="w-4 h-4" />}
                                            {r.service_type === "flight" ? "Uçak Bileti" : "Vize"}
                                        </span>
                                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border ${STATUS_COLOR[r.status] || ""}`}>
                                            {(STATUS.find(([k]) => k === r.status) || [])[1] || r.status}
                                        </span>
                                        <span className="text-[11px] text-muted_green">{fmtDT(r.created_at)}</span>
                                    </div>
                                    <div className="font-serif text-xl">{r.customer_name}</div>
                                    <div className="text-sm text-muted_green flex items-center gap-1.5 mt-0.5">
                                        <Phone className="w-3.5 h-3.5" /> <a className="hover:text-gold" href={`tel:${r.customer_phone}`}>{r.customer_phone}</a>
                                    </div>
                                    <div className="text-sm mt-2 space-x-3">
                                        <span className="text-bone/85">{r.persons} kişi</span>
                                        {r.service_type === "flight" && (r.from_city || r.to_city) && (
                                            <span className="text-bone/85">{r.from_city || "?"} → {r.to_city || "?"}</span>
                                        )}
                                        {r.date_from && <span className="text-bone/85">{r.date_from}{r.date_to ? ` → ${r.date_to}` : ""}</span>}
                                        {r.nationality && <span className="text-muted_green">Uyruk: {r.nationality}</span>}
                                    </div>
                                    {r.notes && <div className="text-xs text-muted_green mt-2 italic">"{r.notes}"</div>}
                                </div>
                                <div className="flex items-center gap-2">
                                    <select
                                        className="input-dark !py-1.5 !text-xs"
                                        value={r.status}
                                        onChange={(e) => setStatus(r, e.target.value)}
                                        data-testid={`sr-status-${r.id}`}
                                    >
                                        {STATUS.map(([k, label]) => <option key={k} value={k} className="bg-emerald_surface">{label}</option>)}
                                    </select>
                                    <button onClick={() => remove(r)} className="text-rose-400 hover:text-rose-500 p-2" data-testid={`sr-del-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminServiceRequests;
